import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";
import { useNotification } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { FiHeart } from "react-icons/fi";

const FavoriteButton = ({ product, size = "md", className = "", ...props }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { refreshData } = useAppData();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [isFavorited, setIsFavorited] = useState(product.is_favorited);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsFavorited(product.is_favorited);
  }, [product.is_favorited]);

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      if (isFavorited) {
        await api.delete(`/favorites/by-product/${product.id}/`);
        showNotification(t("favorites.removed"), "warning");
      } else {
        await api.post("/favorites/", { product: product.id });
        showNotification(t("favorites.added"), "success");
      }
      setIsFavorited(!isFavorited);
      if (refreshData) refreshData();
    } catch (error) {
      console.error("Failed to update favorite", error);
      showNotification(t("favorites.error"), "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`relative flex items-center justify-center transition-all duration-500 disabled:opacity-50 active:scale-75 ${
        size === "sm" ? "w-9 h-9 text-base" : size === "lg" ? "w-14 h-14 text-2xl" : "w-11 h-11 text-xl"
      } rounded-full backdrop-blur-xl border-2 shadow-xl ${
        isFavorited 
          ? "bg-white text-rose-500 border-rose-500 shadow-rose-500/20" 
          : "bg-white/80 text-theme-muted border-white hover:border-rose-200 hover:text-rose-400 shadow-gray-200/20"
      } ${className}`}
      onClick={handleFavoriteClick}
      disabled={loading}
      {...props}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <FiHeart 
          className={`transition-all duration-500 ${isFavorited ? "fill-current scale-110 rotate-[360deg]" : "scale-100"}`} 
        />
      )}
    </button>
  );
};

export default FavoriteButton;
